/*
  Create API Routes group by namespace
  Example:
    Create routes for Event
      events: {
        getEventById: (eventId) => 'events/' + eventId
        createEvent: () => 'events'
        updateEvent: (eventID) => 'events/' + eventId
        deleteEvent: (eventID) => 'events/' + eventId
      }

    Usage at any services

    import { BaseApi, ApiRoutes } from '@/services/BaseApi.js';

    BaseApi('get', ApiRoutes.events.getEventById(eventId));
    BaseApi('post', ApiRoutes.events.createEvent(), event_data);
    BaseApi('put', ApiRoutes.events.updateEvent(eventId), event_data);
    BaseApi('delete', ApiRoutes.events.deleteEvent(eventId));
*/

export const ApiRoutes = {
  auth: {
    login: () => 'login',
    googleAuth: () => 'google-auth',
    auth: () => 'auth',
  },
  corporation: {
    all: () => 'corporations',
    get: (id) => 'corporations/' + id,
    getSchools: (id) => 'corporations/' + id + '/schools',
    create: () => 'corporations',
    update: (id) => 'corporations/' + id,
    changePassword: (id) => 'corporations/' + id + '/change-password',
    frozen: (id) => 'corporations/' + id + '/frozen',
    validateId: (id) => 'corporations/validate/id/' + id,
  },
  brand: {
    all: () => 'brands',
    get: (id) => 'brands/' + id,
    create: () => 'brands',
    update: (id) => 'brands/' + id,
    changePassword: (id) => 'brands/' + id + '/change-password',
    frozen: (id) => 'brands/' + id + '/frozen',
    validateId: (id) => 'brands/validate/id/' + id,
  },
  school: {
    all: () => 'schools',
    get: (id) => 'schools/' + id,
    create: () => 'schools',
    update: (id) => 'schools/' + id,
    changePassword: (id) => 'schools/' + id + '/change-password',
    frozen: (id) => 'schools/' + id + '/frozen',
    videoViewings: (id) => 'schools/' + id + '/videos',
    validateId: (id) => 'schools/validate/id/' + id,
  },
  course: {
    all: () => 'courses',
    get: (id) => 'courses/' + id,
    create: () => 'courses',
    update: (id) => 'courses/' + id,
    frozen: (id) => 'courses/' + id + '/frozen',
  },
  tamaiClass: {
    all: () => 'tamai-classes',
    get: (id) => 'tamai-classes/' + id,
    create: () => 'tamai-classes',
    update: (id) => 'tamai-classes/' + id,
    frozen: (id) => 'tamai-classes/' + id + '/frozen',
  },
  teacher: {
    all: () => 'teachers',
    get: (id) => 'teachers/' + id,
    create: () => 'teachers',
    update: (id) => 'teachers/' + id,
    changePassword: (id) => 'teachers/' + id + '/change-password',
    frozen: (id) => 'teachers/' + id + '/frozen',
    tamaiClasses: (id) => 'teachers/' + id + '/tamai-classes',
    validateId: (id) => 'teachers/validate/id/' + id,
  },
  videoViewing: {
    create: () => 'videos',
    delete: (id) => 'videos/' + id,
    validateId: (id) => 'videos/validate/id/' + id,
  },
  videoViewingPole: {
    get: (id) => 'video-poles/' + id,
    upgradeLevel: (id) => 'video-poles/' + id + '/upgrade-level',
    changePassword: (id) => 'video-poles/' + id + '/change-password',
  },
  student: {
    all: () => 'students',
    get: (id) => 'students/' + id,
    create: () => 'students',
    import: () => 'students/import',
    registerTrial: () => 'students/register-trial',
    update: (id) => 'students/' + id,
    changePassword: (id) => 'students/' + id + '/change-password',
    validateId: (id) => 'students/validate/id/' + id,
    learningHistory: (id) => 'students/' + id + '/learning-history',
    testImages: () => 'students/test-images',
    sendMessage: () => 'students/send-message',
    deleteMessage: (id) => 'messages/' + id,
    certificatePDF: () => 'pdf/certificate',
    changeAffiliation: () => 'students/change-affiliation',
    deactivate: () => 'students/deactivate',
    reactivate: () => 'students/reactivate',
    upgradeLevel: () => 'students/upgrade-level',
    downgradeLevel: () => 'students/downgrade-level',
    upgradeFiguredTeachingMaterial: () => 'students/upgrade-figured-teaching-material',
    upgradeNextFiguredTeachingMaterial: () => 'students/upgrade-next-figured-teaching-material',
    clearNextFiguredTeachingMaterial: () => 'students/clear-next-figured-teaching-material',
    changePreRegistrationTime: () => 'students/change-preregistration-time',
    clearPreRegistrationTime: () => 'students/clear-preregistration-time',
    resetPasswordAndExportPdf: () => 'pdf/id_password',

    // Note: for testing purpose, please delete after finish testing
    // testScheduledFunction: () => 'students/apply-preregistration-reservation',
    // testScheduledFunction: () => 'students/apply-deactivate-reservation',
    // testScheduledFunction: () => 'students/apply-reactivate-reservation',
    // testScheduledFunction: () => 'students/apply-next-figured-teaching-material-reservation',
    // testScheduledFunction: () => 'students/expire-preregistration-reservation',
    // testScheduledFunction: () => 'students/expire-trial',
  },
  frozen: {
    all: () => 'frozen-queues',
    get: (id) => 'frozen-queues/' + id,
    restoreFrozen: (id) => 'frozen-queues/' + id + '/restore',

    // Note: for testing purpose, please delete after finish testing
    // testScheduledFunction: () => 'frozens/apply-frozen-reservation',
    // testScheduledFunction: () => 'frozens/delete-frozen',
    // testScheduledFunction: () => 'frozens/perform-related-frozen',
    // testScheduledFunction: () => 'frozens/perform-related-restore',
  },
  notification: {
    all: () => 'notifications',
    get: (id) => 'notifications/' + id,
    create: () => 'notifications',
    update: (id) => 'notifications/' + id,
    delete: (id) => 'notifications/' + id,
  },
  askTeacher: {
    all: () => 'ask-teachers',
    get: (id) => 'ask-teachers/' + id,
    reply: (id) => 'ask-teachers/' + id + '/reply',
  },
  book: {
    all: () => 'books',
    allParts: (id) => 'books/' + id + '/book-parts',
    allChapters: (_id, pid) => 'book-parts/' + pid + '/book-chapters',
    allBigQuestions: (_id, _pid, cid) => 'book-chapters/' + cid + '/book-big-questions',
  },
  product: {
    all: () => 'products',
  },
  schoolYear: {
    all: () => 'school-years',
  },
  advanceStudent: {
    all: () => 'advance-students',
  },
  billing: {
    all: () => 'billings',
    updateStatus: () => 'billings/update-status',
    fetchExcel: () => 'billings/fetch-excel',
    callExcelGeneration: () => 'billings/call-excel-generation',
  },
  trialCourse: {
    all: () => 'trial-courses',
    create: () => 'trial-courses',
    delete: (id) => 'trial-courses/' + id,
  },
  testPreparationPrint: {
    createPDF: (format) => 'pdf/test_preparation_pdf/' + format,
  },
  mail: {
    sendInquiry: () => 'mail/inquiry',
  },
};
